import React, { useState, useContext } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Card,
  Button,
} from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import OverviewTab from "./OverviewTab";
import ViolationsTab from "./ViolationsTab";
import WorkOrdersTab from "./WorkOrdersTab";
import ArchitecturalRequestsTab from "./ArchitecturalRequestsTab";
import Billing from "./Billing";
import { PermissionsContext } from "../../utils/PermissionsContext";

const ReportsPage = () => {
  const permissionsCtx = useContext(PermissionsContext);

  const hasPermission =
    typeof permissionsCtx === "function"
      ? permissionsCtx
      : (perm) => Array.isArray(permissionsCtx) && permissionsCtx.includes(perm);

  const tabConfig = [
    { label: "Overview", component: <OverviewTab />, permission: null },
    {
      label: "Violations",
      component: <ViolationsTab />,
      permission: "view_violations",
    },
    {
      label: "Work Orders",
      component: <WorkOrdersTab />,
      permission: "view_work_orders",
    },
    {
      label: "Architectural Requests",
      component: <ArchitecturalRequestsTab />,
      permission: "view_architectural_requests",
    },
    {
      label: "Billing",
      component: <Billing />,
      permission: "view_work_orders",
    },
  ];

  const visibleTabs = tabConfig.filter(
    ({ permission }) => !permission || hasPermission(permission)
  );

  const [currentTab, setCurrentTab] = useState(0);

  const handleChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
    overflow: "hidden",
  };

  if (visibleTabs.length === 0) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Card sx={pageCardSx}>
          <Box sx={{ height: 5, backgroundColor: "#2f63ad" }} />
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              textAlign: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#223142", mb: 1 }}
            >
              Reports
            </Typography>
            <Typography sx={{ color: "#6b778c" }}>
              You do not have permission to view any reports.
            </Typography>
          </Box>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Card sx={pageCardSx}>
        <Box sx={{ height: 5, backgroundColor: "#2f63ad" }} />

        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: 3,
            pb: 2,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <AssessmentOutlinedIcon sx={{ color: "#2f63ad", fontSize: 22 }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#223142",
                lineHeight: 1.2,
              }}
            >
              Reports
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review reporting dashboards and analytics by module.
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2 }}>
          <Tabs
            value={currentTab}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Reports Tabs"
            sx={{
              minHeight: "48px",
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "999px",
                backgroundColor: "#2f63ad",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                color: "#5f6b7a",
                minHeight: "48px",
                borderRadius: "10px",
                px: 2,
              },
              "& .Mui-selected": {
                color: "#223142 !important",
              },
            }}
          >
            {visibleTabs.map((tab, index) => (
              <Tab key={index} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        <Box
          sx={{
            p: { xs: 1.5, md: 2.5 },
          }}
        >
          {visibleTabs[currentTab]?.component}
        </Box>
      </Card>
    </Box>
  );
};

export default ReportsPage;